/* eslint-disable react/no-danger */
import dynamic from 'next/dynamic';
import { END } from 'redux-saga';
import { useEffect, useMemo, useRef, useState } from 'react';
import { actions, GET_DETAIL_PROJECT } from '@containers/Home/slice';
import { wrapper, SagaStore } from 'store';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentDomain, isCustomDomain } from '@utils/domainHelper';
import fs from 'fs';
import path from 'path';
import {
  selectConfigLittlePlanetProject,
  selectConfigFirstSceneProject,
  selectFirstSceneMediaProject,
  selectGATagTrackingProject,
  selectFeatureImageProject,
  selectGATrackingProject,
  selectThumbnailProject,
  selectMetaDataProject,
  selectTitleProject,
  selectListScene,
  selectDetailProject,
  selectConfigSoundProject,
} from '@containers/Home/selectors';
import Script from 'next/script';

const Home = dynamic(() => import('containers/Home'), {
  loading: () => <LoadingAnimation message="Đang khởi tạo tour..." />,
});

const ModalConfirm = dynamic(() => import('@components/ModalConfirm'));
const LoadingAnimation = dynamic(() => import('@components/LoadingAnimation'));

function PublicContainer() {
  const router = useRouter();
  const dispatch = useDispatch();
  const [visibleSoundConfirm, setVisibleSoundConfirm] = useState(false);
  const hasCalledApiRef = useRef(false);

  const title = useSelector(selectTitleProject);
  const listScene = useSelector(selectListScene);
  const metadata = useSelector(selectMetaDataProject);
  const thumbnail = useSelector(selectThumbnailProject);
  const ga_tracking = useSelector(selectGATrackingProject);
  const feature_image = useSelector(selectFeatureImageProject);
  const first_scene = useSelector(selectConfigFirstSceneProject);
  const ggtag_tracking = useSelector(selectGATagTrackingProject);
  const first_scene_media = useSelector(selectFirstSceneMediaProject);
  const config_little_planet = useSelector(selectConfigLittlePlanetProject);
  const detailProject = useSelector(selectDetailProject);
  const configSound = useSelector(selectConfigSoundProject);

  // Kiểm tra xem có âm thanh không
  const hasBackgroundSound = useMemo(() => {
    return !!detailProject?.background_sound;
  }, [detailProject]);

  // Xử lý khi đồng ý bật âm thanh
  const handleSoundConfirmOK = () => {
    dispatch(actions.setConfigSoundProject({ on: true }));
    setVisibleSoundConfirm(false);
  };

  // Xử lý khi từ chối bật âm thanh
  const handleSoundConfirmCancel = () => {
    dispatch(actions.setConfigSoundProject({ on: false }));
    setVisibleSoundConfirm(false);
  };

  // Fetch project data on client-side based on domain detection
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !hasCalledApiRef.current &&
      router.query.tour
    ) {
      const domain = getCurrentDomain();
      const isCustomDomainPage = isCustomDomain(domain);

      hasCalledApiRef.current = true;

      if (isCustomDomainPage) {
        console.log(
          '[Tour Page] Custom domain detected, fetching project with domain:',
          domain,
        );
        dispatch(
          GET_DETAIL_PROJECT({
            domain,
            isPublic: true,
          }),
        );
      } else {
        console.log(
          '[Tour Page] Main domain detected, fetching project with slug:',
          router.query.tour,
        );
        dispatch(
          GET_DETAIL_PROJECT({
            id: router.query.tour,
            isPublic: true,
          }),
        );
      }
    }
  }, [dispatch, router.query.tour]);

  // Hiển thị popup âm thanh khi trang được tải
  useEffect(() => {
    if (
      hasBackgroundSound &&
      (configSound?.on === undefined || configSound?.on === null)
    ) {
      console.log(
        '[DEBUG] Showing sound confirmation from tour PublicContainer',
      );
      setVisibleSoundConfirm(true);
    }
  }, [hasBackgroundSound, configSound]);

  const detailFirstScene = useMemo(
    () => listScene.filter(item => item.id === first_scene)[0] || undefined,
    [listScene, first_scene],
  );

  const images = useMemo(() => {
    const imgs: any = [];
    if (thumbnail?.src)
      imgs.push({
        url: thumbnail?.src,
        width: 800,
        height: 600,
        alt: thumbnail?.name,
      });

    if (feature_image)
      imgs.push({
        url: feature_image,
        width: 800,
        height: 600,
        alt: '',
      });

    if (first_scene_media?.src)
      imgs.push({
        url:
          first_scene_media?.src_thumb ||
          first_scene_media?.src_preview ||
          first_scene_media?.src,
        width: 800,
        height: 600,
        alt: first_scene_media?.name,
      });

    return imgs;
  }, [thumbnail, feature_image, first_scene_media]);

  useEffect(() => {
    if (listScene && listScene.length) {
      let scene = detailFirstScene
        ? { ...detailFirstScene }
        : { ...listScene[0] };
      if (router.query.scene && router.query.scene.length) {
        const selectedScene = listScene.filter(
          item => item.id === router.query.scene[0],
        )[0];
        scene = selectedScene ? { ...selectedScene } : { ...listScene[0] };
      }
      // NOTE: add intro little planet for first scene loaded
      scene.config = scene?.config || {};
      scene.config = {
        ...scene.config,
        isLittlePlanet: config_little_planet?.enable || false,
        timeTransitionLittlePlanet: config_little_planet?.transition_time || 2,
      };
      dispatch(actions.setSceneSelected(scene));
      // router.push(
      //   `${process.env.NEXT_PUBLIC_BASE_URL_PUBLIC}/${router.query.tour}/${scene.id}`,
      //   undefined,
      //   { shallow: true },
      // );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listScene]);

  // Handle fallback state
  if (router.isFallback) {
    return <LoadingAnimation />;
  }

  return (
    <>
      <NextSeo
        title={metadata?.title || title}
        description={metadata?.description || 'Panoee - Virtual Tour 360'}
        openGraph={{
          type: 'website',
          url: process.env.NEXT_PUBLIC_BASE_URL + router.asPath,
          title: metadata?.title || title,
          description: metadata?.description || 'Panoee - Virtual Tour 360',
          images,
          site_name: 'Panoee Tour',
          profile: {
            username: router.query.username as string,
          },
        }}
        facebook={{
          appId: process.env.NEXT_PUBLIC_FB_APP_ID || '2086263614924092',
        }}
      />
      {ga_tracking && (
        <>
          <Script
            strategy="beforeInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${ga_tracking}`}
          />
          <Script
            id="gg-tag"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${ga_tracking}', {
                  page_path: window.location.pathname,
                });
              `,
            }}
          />
        </>
      )}
      {ggtag_tracking && (
        <>
          <Script
            strategy="beforeInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${ggtag_tracking}`}
          />
          <Script
            id="gg-tag-1"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', '${ggtag_tracking}');
              `,
            }}
          />
        </>
      )}
      <Home isPublic />

      {/* Thêm Modal xác nhận âm thanh trực tiếp ở đây */}
      {visibleSoundConfirm && (
        <ModalConfirm
          visible={visibleSoundConfirm}
          onOK={handleSoundConfirmOK}
          onCancel={handleSoundConfirmCancel}
        />
      )}
    </>
  );
}

export const getStaticPaths = async () => {
  const dataJsonPath = path.join(
    process.cwd(),
    'public',
    'mock-data',
    'data.json',
  );
  const paths = [];

  try {
    if (fs.existsSync(dataJsonPath)) {
      const fileContent = fs.readFileSync(dataJsonPath, 'utf8');
      const jsonData = JSON.parse(fileContent);

      if (jsonData && jsonData.project && jsonData.project.slug) {
        const tourSlug = jsonData.project.slug;

        // Path for the main tour page (e.g., /tour-slug/)
        // This will make router.query.scene an empty array
        paths.push({
          params: { tour: tourSlug, scene: [] },
        });

        // Paths for each scene in the tour (e.g., /tour-slug/scene-id)
        if (jsonData.project.scenes && Array.isArray(jsonData.project.scenes)) {
          jsonData.project.scenes.forEach(scene => {
            if (scene && scene.id) {
              paths.push({
                params: { tour: tourSlug, scene: [String(scene.id)] },
              });
            }
          });
        }
      } else {
        console.warn(
          'data.json does not contain project.slug. No paths will be generated.',
        );
      }
    } else {
      console.warn(
        `data.json not found at ${dataJsonPath}. No paths will be generated.`,
      );
    }
  } catch (error) {
    console.error(
      'Error reading or parsing data.json in getStaticPaths:',
      error,
    );
  }

  return {
    paths,
    fallback: true, // Set back to false to allow static export
  };
};

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params }) => {
      try {
        if (params?.tour) {
          store.dispatch(
            GET_DETAIL_PROJECT({
              id: params.tour,
              isPublic: true,
            }),
          );

          store.dispatch(END);
          await (store as SagaStore).sagaTask?.toPromise();

          return {
            props: {},
            revalidate: 60, // Revalidate every 60 seconds
          };
        }

        return {
          notFound: true,
        };
      } catch (error) {
        console.error('Error in getStaticProps:', error);
        return {
          notFound: true,
        };
      }
    },
);

export default PublicContainer;
